/* ═══════════════════════════════════════════════════════════════
   PhasePanel — manage a project's phases (Sheet). Add from the
   firm-wide default list or a custom name, edit dates inline, reorder
   with up/down buttons, remove with a confirm. Never touches the
   firm-wide admin default-phases list itself (ctx.firmPhases) — this
   panel only ever writes project-scoped ProjectPhase rows.
   ═══════════════════════════════════════════════════════════════ */
import React, { useEffect, useRef, useState } from 'react';
import { ArrowUp, ArrowDown, X, Plus } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from '../../../components/ui/sheet';
import { ConfirmDialog } from '../../../components/ui/confirm-dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { SearchableSelect } from '../../../components/SearchableSelect';
import { Icon } from '../../../components/ui/Icon';
import { rem } from '../../../lib/rem';
import type { ProjectPhase } from '../../../../shared/entities';
import type { ProjectWidgetCtx } from '../widgets/context';

const CUSTOM_VALUE = '__custom__';

export function PhasePanel({
  ctx,
  open,
  onClose,
}: {
  ctx: ProjectWidgetCtx;
  open: boolean;
  onClose: () => void;
}) {
  const [pendingDelete, setPendingDelete] = useState<ProjectPhase | null>(null);
  const [addPick, setAddPick] = useState('');
  const [customName, setCustomName] = useState('');
  const [busy, setBusy] = useState(false);

  // Panel stays mounted across close/open (focus-restore fix) — reset
  // ephemeral UI state each time it opens rather than relying on a remount.
  useEffect(() => {
    if (open) {
      setPendingDelete(null);
      setAddPick('');
      setCustomName('');
    }
  }, [open]);

  // Radix's Dialog only restores focus to a <Dialog.Trigger> ref on close —
  // this kit's Sheets are always opened imperatively from a page-owned
  // button, never a <SheetTrigger>, so that ref is always null and focus
  // silently lands on <body> (DESIGN.md §7). Capture whatever was focused
  // right before open and restore it ourselves via onCloseAutoFocus.
  const openerRef = useRef<Element | null>(null);
  useEffect(() => {
    if (open) openerRef.current = document.activeElement;
  }, [open]);

  const phases = [...ctx.phases.data].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
  const canEdit = ctx.can.edit;
  const existingNames = new Set(phases.map((p) => p.name));
  const addOptions = [
    ...ctx.firmPhases
      .filter((fp) => !existingNames.has(fp.name))
      .map((fp) => ({ value: fp.name, label: fp.name })),
    { value: CUSTOM_VALUE, label: 'Custom…' },
  ];

  async function addPhase(name: string) {
    if (!name.trim()) return;
    setBusy(true);
    try {
      await ctx.savePhase({
        id: '',
        projectId: ctx.project.id,
        name: name.trim(),
        description: '',
        status: 'pending',
        sortOrder: phases.length,
        createdAt: '',
        updatedAt: '',
      });
      setAddPick('');
      setCustomName('');
    } finally {
      setBusy(false);
    }
  }

  async function move(idx: number, dir: -1 | 1) {
    const target = idx + dir;
    if (target < 0 || target >= phases.length) return;
    const ordered = phases.map((p) => p.id);
    [ordered[idx], ordered[target]] = [ordered[target], ordered[idx]];
    setBusy(true);
    try {
      await ctx.reorderPhases(ordered);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
        <SheetContent
          side="right"
          className="!w-[clamp(24rem,36vw,40rem)]"
          onCloseAutoFocus={(e) => {
            e.preventDefault();
            if (openerRef.current instanceof HTMLElement) openerRef.current.focus();
          }}
        >
          <SheetHeader>
            <SheetTitle>Manage phases</SheetTitle>
          </SheetHeader>

          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--space-3)',
              marginTop: 'var(--space-4)',
              flex: 1,
              overflowY: 'auto',
            }}
          >
            {phases.length === 0 && (
              <div
                style={{
                  fontSize: 'var(--font-size-base)',
                  color: 'var(--tm)',
                  fontStyle: 'italic',
                }}
              >
                No phases yet — add the first one below.
              </div>
            )}
            {phases.map((p, idx) => (
              <div
                key={p.id}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 'var(--space-2)',
                  padding: 'var(--space-3)',
                  border: '1px solid var(--bd)',
                  borderRadius: 'var(--radius-md)',
                  background: 'var(--sa)',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                  <span
                    style={{
                      flex: 1,
                      fontWeight: 600,
                      fontSize: 'var(--font-size-base)',
                      color: 'var(--tx)',
                    }}
                  >
                    {p.name}
                  </span>
                  <span
                    style={{
                      fontSize: 'var(--font-size-xs)',
                      fontWeight: 700,
                      padding: `1px ${rem(8)}`,
                      borderRadius: 'var(--radius-full)',
                      color:
                        p.status === 'complete'
                          ? 'var(--ok)'
                          : p.status === 'in-progress'
                            ? 'var(--color-info)'
                            : 'var(--tm)',
                      background:
                        p.status === 'complete'
                          ? 'color-mix(in srgb, var(--ok) 14%, transparent)'
                          : p.status === 'in-progress'
                            ? 'color-mix(in srgb, var(--color-info) 14%, transparent)'
                            : 'var(--sf)',
                    }}
                  >
                    {p.status}
                  </span>
                  {canEdit && (
                    <>
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label={`Move ${p.name} up`}
                        disabled={idx === 0 || busy}
                        onClick={() => move(idx, -1)}
                      >
                        <Icon icon={ArrowUp} size="sm" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label={`Move ${p.name} down`}
                        disabled={idx === phases.length - 1 || busy}
                        onClick={() => move(idx, 1)}
                      >
                        <Icon icon={ArrowDown} size="sm" />
                      </Button>
                    </>
                  )}
                  {/* Server enforces `projects.delete` for phase removal (not
                      `projects.edit`) — gate the control to match so it never
                      renders a button that would 403. The CURRENT phase can't
                      be deleted (server 409) — disabled, not hidden, so the
                      reason is discoverable. */}
                  {ctx.can.deleteProject && (
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={`Remove ${p.name}`}
                      title={
                        p.id === ctx.project.currentPhaseId ? 'Set another phase first' : undefined
                      }
                      disabled={p.id === ctx.project.currentPhaseId}
                      onClick={() => setPendingDelete(p)}
                    >
                      <Icon icon={X} size="sm" className="text-danger" />
                    </Button>
                  )}
                </div>
                {canEdit && (
                  <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                    <Input
                      type="date"
                      aria-label={`${p.name} start date`}
                      defaultValue={p.startDate || ''}
                      onBlur={(e) => {
                        const v = e.target.value || undefined;
                        if (v !== p.startDate) void ctx.savePhase({ ...p, startDate: v });
                      }}
                    />
                    <Input
                      type="date"
                      aria-label={`${p.name} end date`}
                      defaultValue={p.endDate || ''}
                      onBlur={(e) => {
                        const v = e.target.value || undefined;
                        if (v !== p.endDate) void ctx.savePhase({ ...p, endDate: v });
                      }}
                    />
                  </div>
                )}
              </div>
            ))}

            {canEdit && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                <SearchableSelect
                  value={addPick}
                  options={addOptions}
                  onChange={(v) => {
                    setAddPick(v);
                    if (v && v !== CUSTOM_VALUE) void addPhase(v);
                  }}
                  placeholder="Add a phase…"
                  disabled={busy}
                />
                {addPick === CUSTOM_VALUE && (
                  <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                    <Input
                      autoFocus
                      placeholder="Custom phase name"
                      value={customName}
                      onChange={(e) => setCustomName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') void addPhase(customName);
                      }}
                    />
                    <Button
                      size="sm"
                      onClick={() => addPhase(customName)}
                      disabled={!customName.trim() || busy}
                    >
                      <Icon icon={Plus} size="sm" /> Add
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>

          <SheetFooter>
            <Button variant="outline" onClick={onClose}>
              Done
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      <ConfirmDialog
        open={!!pendingDelete}
        onOpenChange={(o) => !o && setPendingDelete(null)}
        title="Remove phase?"
        description={`"${pendingDelete?.name}" will be removed from this project. This can't be undone.`}
        confirmLabel="Remove"
        destructive
        onConfirm={async () => {
          if (!pendingDelete) return;
          await ctx.deletePhase(pendingDelete.id);
          setPendingDelete(null);
        }}
      />
    </>
  );
}
